import { useState, useEffect, useRef } from "react";
import {
  Modal,
  TextInput,
  Select,
  Stack,
  Text,
  Group,
  Button,
  Badge,
} from "@mantine/core";

interface MacroVariableModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (variables: Record<string, string>) => void;
  variables: string[];
  macroCode: string;
  locationValue?: string | null; // Pre-fill LOCATION if available
}

// Common variable options (exported for inline variable fields)
export const MACRO_LOCATION_OPTIONS = [
  { value: "ESOPHAGUS", label: "Esophagus" },
  { value: "STOMACH", label: "Stomach" },
  { value: "DUODENUM", label: "Duodenum" },
  { value: "CECUM", label: "Cecum" },
  { value: "ASCENDING", label: "Ascending Colon" },
  { value: "TRANSVERSE", label: "Transverse Colon" },
  { value: "DESCENDING", label: "Descending Colon" },
  { value: "SIGMOID", label: "Sigmoid Colon" },
  { value: "RECTUM", label: "Rectum" },
];

export const MACRO_QUANTITY_OPTIONS = [
  { value: "scant", label: "Scant" },
  { value: "few", label: "Few" },
  { value: "moderate", label: "Moderate" },
  { value: "abundant", label: "Abundant" },
  { value: "numerous", label: "Numerous" },
];

export const MACRO_FRAGMENT_OPTIONS = [
  {
    value: "single_fragment",
    label: "A single fragment of tissue was received.",
  },
  {
    value: "multiple_fragments",
    label: "Multiple fragments of tissue were received.",
  },
  {
    value: "reportedly_polyps",
    label:
      "Reportedly [X] polyps were removed; [Y] fragments of adenomatous tissue received, suggesting fragmentation or piecemeal removal of at least one polyp.",
  },
];

export default function MacroVariableModal({
  opened,
  onClose,
  onSubmit,
  variables,
  macroCode,
  locationValue,
}: MacroVariableModalProps) {
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {}
  );
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Initialize variable values when modal opens
  useEffect(() => {
    if (opened) {
      const initial: Record<string, string> = {};
      variables.forEach((v) => {
        // Pre-fill LOCATION if we have a value
        if (v === "LOCATION" && locationValue) {
          initial[v] = locationValue.toUpperCase();
        } else {
          initial[v] = "";
        }
      });
      setVariableValues(initial);
      // Focus first input after modal animation
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [opened, variables, locationValue]);

  const handleSubmit = () => {
    onSubmit(variableValues);
    onClose();
  };

  const updateVariable = (name: string, value: string) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }));
  };

  const renderVariableInput = (variable: string, index: number) => {
    const value = variableValues[variable] || "";

    // Special handling for known variables
    if (variable === "LOCATION") {
      return (
        <Select
          key={variable}
          ref={index === 0 ? (firstInputRef as any) : undefined}
          label={`[[${variable}]]`}
          placeholder="Select location"
          value={value}
          onChange={(val) => updateVariable(variable, val || "")}
          data={MACRO_LOCATION_OPTIONS}
          searchable
          clearable
        />
      );
    }

    if (variable === "QUANTITY") {
      return (
        <Select
          key={variable}
          ref={index === 0 ? (firstInputRef as any) : undefined}
          label={`[[${variable}]]`}
          placeholder="Select quantity"
          value={value}
          onChange={(val) => updateVariable(variable, val || "")}
          data={MACRO_QUANTITY_OPTIONS}
          searchable
          clearable
        />
      );
    }

    if (variable === "FRAGMENTS") {
      return (
        <Select
          key={variable}
          ref={index === 0 ? (firstInputRef as any) : undefined}
          label={`[[${variable}]]`}
          placeholder="Select fragment description"
          value={value}
          onChange={(val) => updateVariable(variable, val || "")}
          data={MACRO_FRAGMENT_OPTIONS}
          clearable
        />
      );
    }

    // Default: free text input
    return (
      <TextInput
        key={variable}
        ref={index === 0 ? firstInputRef : undefined}
        label={`[[${variable}]]`}
        placeholder={`Enter value for ${variable}`}
        value={value}
        onChange={(e) => updateVariable(variable, e.target.value)}
      />
    );
  };

  const allFilled = variables.every((v) => variableValues[v]?.trim());

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Text fw={600}>Fill in Template Variables</Text>
          <Badge variant="light">{macroCode}</Badge>
        </Group>
      }
      size="md"
      centered
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          This macro contains variables that need to be filled in. Enter values
          for each variable below.
        </Text>

        {variables.map((variable, index) =>
          renderVariableInput(variable, index)
        )}

        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!allFilled}>
            Apply Macro
          </Button>
        </Group>

        {!allFilled && (
          <Text size="xs" c="orange" ta="center">
            Please fill in all variables to continue
          </Text>
        )}
      </Stack>
    </Modal>
  );
}

// Utility function to extract variables from macro text
export function extractMacroVariables(text: string): string[] {
  const regex = /\[\[([A-Z_]+)\]\]/g;
  const matches = text.match(regex) || [];
  const variables: string[] = [];
  matches.forEach((m) => {
    const varName = m.replace(/\[\[|\]\]/g, "");
    if (!variables.includes(varName)) {
      variables.push(varName);
    }
  });
  return variables;
}

// Utility function to replace variables in macro text
export function replaceMacroVariables(
  text: string,
  variables: Record<string, string>
): string {
  let result = text;
  Object.entries(variables).forEach(([name, value]) => {
    result = result.replace(new RegExp(`\\[\\[${name}\\]\\]`, "g"), value);
  });
  return result;
}
